import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Upload, 
  Download, 
  Trash2, 
  Eye,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const ResumeUpload = () => {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const response = await api.get('/api/profile/resumes/');
      console.log('Resumes response:', response.data);
      
      // Handle different response structures
      const resumesData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setResumes(resumesData);
    } catch (error) {
      toast.error('Failed to fetch resumes');
      console.error('Error fetching resumes:', error);
      // Set empty array as fallback
      setResumes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or Word document');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('is_primary', resumes.length === 0); // First resume is primary by default

      const response = await api.post('/api/profile/resumes/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Resume uploaded successfully');
      fetchResumes();
    } catch (error) {
      toast.error('Failed to upload resume');
      console.error('Error uploading resume:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) {
      return;
    }

    try {
      await api.delete(`/api/profile/resumes/${id}/`);
      toast.success('Resume deleted successfully');
      fetchResumes();
    } catch (error) {
      toast.error('Failed to delete resume');
      console.error('Error deleting resume:', error);
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await api.patch(`/api/profile/resumes/${id}/`, { is_primary: true });
      toast.success('Primary resume updated');
      fetchResumes();
    } catch (error) {
      toast.error('Failed to update primary resume');
      console.error('Error updating primary resume:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900">Resume Management</h1>
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Upload className="w-6 h-6 text-gray-600" />
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Upload your resume
                  </h3>
                  <p className="text-sm text-gray-500">
                    Drag and drop your resume here, or click to browse
                  </p>
                </div>

                <div>
                  <label className="btn-primary cursor-pointer">
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileInput}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>

                <div className="text-xs text-gray-500">
                  <p>Supported formats: PDF, DOC, DOCX</p>
                  <p>Maximum file size: 5MB</p>
                </div>
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                  <span className="text-sm text-blue-700">Uploading resume...</span>
                </div>
              </div>
            )}

            {/* Resumes List */}
            {resumes.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Resumes</h3>
                <div className="space-y-4">
                  {Array.isArray(resumes) && resumes.map((resume) => (
                    <div key={resume.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <h4 className="text-sm font-medium text-gray-900">
                                {resume.name}
                              </h4>
                              {resume.is_primary && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Primary
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>{formatFileSize(resume.file_size)}</span>
                              <span>•</span>
                              <span>Uploaded {formatDate(resume.uploaded_at)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {!resume.is_primary && (
                            <button
                              onClick={() => handleSetPrimary(resume.id)}
                              className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                              title="Set as primary"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          
                          <a
                            href={resume.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="View resume"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          
                          <a
                            href={resume.file_url}
                            download
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Download resume"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          
                          <button
                            onClick={() => handleDelete(resume.id)}
                            className="p-2 text-red-400 hover:text-red-600 transition-colors"
                            title="Delete resume"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {resumes.length === 0 && !uploading && (
              <div className="mt-8 text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes uploaded</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Upload your resume to apply for jobs easily and showcase your qualifications.
                </p>
              </div>
            )}

            {/* Tips */}
            <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="w-5 h-5 text-yellow-400 mr-3 mt-0.5" />
                <div className="text-sm text-yellow-700">
                  <h4 className="font-medium mb-1">Tips for a great resume:</h4>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Use a clear, professional format</li>
                    <li>Include relevant keywords from job descriptions</li>
                    <li>Keep it concise (1-2 pages for most roles)</li>
                    <li>Update your resume regularly with new experiences</li>
                    <li>Save as PDF to preserve formatting</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeUpload;
